import React, { useState, useEffect } from 'react';
import { 
  Search, 
  BookOpen, 
  Clock, 
  HelpCircle, 
  Lock, 
  Play, 
  CheckCircle2, 
  MapPin, 
  Sparkles,
  FileText,
  Layers,
  ArrowLeft,
  PenTool,
  AlertCircle
} from 'lucide-react';
import { UserProfile, PraxisPaper, MockExam, TestAttempt } from '../types';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { STATE_STANDARDS } from '../data/stateStandards';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { ExamsDirectory } from './ExamsDirectory';
import { ExamDetailPage } from './ExamDetailPage';
import { getPaperCatalogSummaries, isPaperUnlockedForUser } from '../lib/firebase';

interface PaperTabsProps {
  userProfile: UserProfile;
  onStartTest: (paperId: string) => void;
  onNavigateToMockExams?: (familyOrState: string) => void;
  onOpenGuides: (paperId: string) => void;
  onOpenResources?: (paperId: string) => void;
  onTryFree?: (paperId: string) => void;
  openPricingModal: () => void;
  selectedExamEntry?: ExamCatalogEntry | null;
  onSelectExam?: (entry: ExamCatalogEntry) => void;
  onClearSelectedExam?: () => void;
  // 2026-10-07 navy redesign: data for the new Exams Directory landing view.
  catalogPapers?: PraxisPaper[];
  testAttempts?: TestAttempt[];
  dueReviewCount?: number;
  onOpenTutoring?: () => void;
  onOpenReview?: () => void;
}

export const PaperTabs: React.FC<PaperTabsProps> = ({
  userProfile,
  onStartTest,
  onNavigateToMockExams,
  onOpenGuides,
  onOpenResources,
  onTryFree,
  openPricingModal,
  selectedExamEntry: externalSelectedExam,
  onSelectExam: externalOnSelectExam,
  onClearSelectedExam: externalOnClearSelectedExam,
  catalogPapers,
  testAttempts,
  dueReviewCount,
  onOpenTutoring,
  onOpenReview
}) => {
  const [papers, setPapers] = useState<PraxisPaper[]>(PRAXIS_PAPERS);
  const [loading, setLoading] = useState<boolean>(true);
  const [internalSelectedExam, setInternalSelectedExam] = useState<ExamCatalogEntry | null>(null);
  const [viewMode, setViewMode] = useState<'catalog' | 'detail' | 'individual'>('catalog');
  
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const currentSelectedExam = externalSelectedExam !== undefined ? externalSelectedExam : internalSelectedExam;

  useEffect(() => {
    async function loadPapers() {
      try {
        const firestorePapers = await getPaperCatalogSummaries();
        if (firestorePapers && firestorePapers.length > 0) {
          setPapers(firestorePapers);
        }
      } catch (err) {
        console.warn('PaperTabs: could not load papers from Firestore, using static list:', err);
      } finally {
        setLoading(false);
      }
    }
    loadPapers();
  }, []);

  const handleSelectExam = (entry: ExamCatalogEntry) => {
    if (externalOnSelectExam) {
      externalOnSelectExam(entry);
    } else {
      setInternalSelectedExam(entry);
    }
    setViewMode('detail');
  };

  const handleClearSelectedExam = () => {
    if (externalOnClearSelectedExam) {
      externalOnClearSelectedExam();
    } else {
      setInternalSelectedExam(null);
    }
    setViewMode('catalog');
  };

  const activeState = STATE_STANDARDS.find(s => s.code === userProfile.targetState) || STATE_STANDARDS[0];

  // If a specific state exam entry is selected, render the ExamDetailPage
  if (currentSelectedExam && viewMode !== 'individual') {
    return (
      <ExamDetailPage
        entry={currentSelectedExam}
        userProfile={userProfile}
        onStartTest={onStartTest}
        onNavigateToMockExams={onNavigateToMockExams}
        onOpenGuides={onOpenGuides}
        onOpenResources={onOpenResources}
        onTryFree={onTryFree}
        openPricingModal={openPricingModal}
        onBackToCatalog={handleClearSelectedExam}
      />
    );
  }

  // If viewMode is 'catalog' (default), render the Exams Directory (2026-10-07 navy redesign)
  if (viewMode === 'catalog') {
    return (
      <ExamsDirectory
        papers={catalogPapers && catalogPapers.length > 0 ? catalogPapers : papers}
        userProfile={userProfile}
        testAttempts={testAttempts}
        dueReviewCount={dueReviewCount}
        onSelectExam={handleSelectExam}
        onViewAllIndividualPapers={() => setViewMode('individual')}
        onStartTest={onStartTest}
        onOpenTutoring={onOpenTutoring}
        onOpenReview={onOpenReview}
      />
    );
  }

  // Individual papers view (fallback / detailed paper list)
  const categories = [
    { id: 'all', label: 'All PRAXIS Papers' },
    { id: 'core', label: 'Core Academic Skills (5713/5723/5733)' },
    { id: 'elementary', label: 'Elementary Education (5001/5002-5005)' },
    { id: 'plt', label: 'Principles of Learning & Teaching (PLT K-12)' },
    { id: 'subject', label: 'Subject Specializations' }
  ];

  const filteredPapers = papers.filter(paper => {
    if (Array.isArray(paper.bundleOf) && paper.bundleOf.length > 0) return false;
    const matchesCategory = activeCategory === 'all' || paper.category === activeCategory;
    const matchesSearch = paper.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          paper.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          paper.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200">
        <div>
          <button
            onClick={() => setViewMode('catalog')}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to State Exam Directory
          </button>
          <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-wider">
            <BookOpen className="w-4 h-4" /> All Individual Papers List
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-800 mt-1">
            Individual Exam Papers Catalog
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Search or filter individual practice test papers by subject area or code.
          </p>
        </div>

        {/* State Banner */}
        <div className="bg-white border border-slate-200 shadow-xs rounded-xl p-3.5 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center font-bold">
              <MapPin className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="text-xs">
              <div className="font-bold text-slate-800">
                Active State: <span className="text-indigo-600">{activeState.name} ({activeState.code})</span>
              </div>
              <div className="text-slate-400 text-[11px]">
                {activeState.note}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter & Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-4">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="relative min-w-[260px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by code (e.g., 5733, PLT)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
            />
          </div>
        </div>
      </div>

      {/* Grid of Individual Papers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPapers.map((paper) => {
          const stateScore = activeState.passingScores[paper.code] ?? (paper.passingScoreEts !== null && paper.passingScoreEts !== undefined ? paper.passingScoreEts : null);
          const isUnlocked = isPaperUnlockedForUser(userProfile, paper.code) || isPaperUnlockedForUser(userProfile, paper.id);
          const isEssayPaper = paper.contentType === 'essay' || (Array.isArray(paper.essayPrompts) && paper.essayPrompts.length > 0);
          const isMixedPaper = paper.contentType === 'mixed';
          const isPerformanceAssessment = paper.contentType === 'performance_assessment';
          const promptCount = paper.essayPrompts?.length || 0;

          // Compute dynamic MC & CR question counts across domains
          const totalMcCount = paper.domains && paper.domains.length > 0 && paper.domains.some(d => d.mcCount !== undefined)
            ? paper.domains.reduce((sum, d) => sum + (d.mcCount || 0), 0)
            : (paper.questions?.length || paper.totalQuestions || 0);

          const totalCrCount = paper.domains && paper.domains.length > 0 && paper.domains.some(d => d.crCount !== undefined)
            ? paper.domains.reduce((sum, d) => sum + (d.crCount || 0), 0)
            : (paper.constructedResponsePrompts?.length || 0);

          return (
            <div
              key={paper.id}
              className="rounded-xl border border-slate-200 bg-white shadow-xs hover:border-indigo-200 transition-all flex flex-col justify-between overflow-hidden"
            >
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">
                      {paper.examCode && !paper.examCode.toLowerCase().startsWith('praxis')
                        ? (paper.code.toLowerCase().startsWith('nystce-') || paper.code.toLowerCase().startsWith('cset-') || paper.code.toLowerCase().startsWith('cbest-')
                            ? paper.code
                            : `${paper.examCode} ${paper.code}`)
                        : `PRAXIS ${paper.code}`}
                    </span>
                    {isEssayPaper && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-900 font-bold text-[10px] rounded border border-amber-200">
                        Essay
                      </span>
                    )}
                    {isMixedPaper && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 font-bold text-[10px] rounded border border-emerald-200">
                        MC + CR
                      </span>
                    )}
                    {isPerformanceAssessment && (
                      <span className="px-2 py-0.5 bg-sky-100 text-sky-900 font-bold text-[10px] rounded border border-sky-200">
                        Portfolio
                      </span>
                    )}
                  </div>
                  {paper.isPassingScoreProvisional || paper.passingScoreNote ? (
                    <span className="text-xs font-semibold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200" title={paper.passingScoreNote}>
                      Target Score: {stateScore || 240} (provisional)
                    </span>
                  ) : stateScore !== null ? (
                    <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                      Target Score: {stateScore}
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 italic">
                      Passing score: Not yet confirmed
                    </span>
                  )}
                </div>

                <div>
                  <h3 className="text-base font-bold text-slate-800 line-clamp-1">
                    {paper.title}
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed mt-1.5 line-clamp-2">
                    {paper.description}
                  </p>
                  {paper.statusNote && (
                    <p className="text-[11px] text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-2 py-1 mt-2 leading-snug">
                      {paper.statusNote}
                    </p>
                  )}
                </div>

                {(paper.isPassingScoreProvisional || paper.passingScoreNote) && stateScore !== null && (
                  <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-950 leading-relaxed flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <span>
                      Passing score: <strong>{stateScore}</strong> ({paper.passingScoreNote || 'provisional — based on other states using this exam; not yet confirmed by the California CTC'})
                    </span>
                  </div>
                )}

                {isPerformanceAssessment ? (
                  <div className="flex items-center justify-between gap-3 text-xs font-medium text-sky-950 bg-sky-50/80 p-2.5 rounded-lg border border-sky-200">
                    <div className="flex items-center gap-1.5 font-semibold">
                      <Layers className="w-3.5 h-3.5 text-sky-700" />
                      <span>Portfolio-Based Assessment</span>
                    </div>
                    <span className="text-[11px] font-bold text-sky-800 bg-white px-2 py-0.5 rounded border border-sky-200">Clinical Practicum</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 text-xs font-medium text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      <span>{paper.durationMinutes && paper.durationMinutes > 0 ? `${paper.durationMinutes} mins` : 'Duration not yet confirmed'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isEssayPaper ? (
                        <>
                          <PenTool className="w-3.5 h-3.5 text-amber-600" />
                          <span className="font-semibold text-amber-900">{promptCount} {promptCount === 1 ? 'Essay Prompt' : 'Essay Prompts'}</span>
                        </>
                      ) : isMixedPaper ? (
                        <>
                          <HelpCircle className="w-3.5 h-3.5 text-indigo-600" />
                          <span className="font-semibold text-slate-800">
                            {totalMcCount} MC Questions · {totalCrCount} Constructed Responses
                          </span>
                        </>
                      ) : (
                        <>
                          <HelpCircle className="w-3.5 h-3.5 text-slate-400" />
                          <span>{totalMcCount || paper.totalQuestions || 0} questions</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 bg-slate-50/80 border-t border-slate-100 flex items-center flex-wrap gap-2">
                {isUnlocked ? (
                  <>
                    <button
                      onClick={() => onStartTest(paper.id)}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2 px-3 rounded-lg transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Play className="w-3.5 h-3.5" /> Start Practice & View Blueprints
                    </button>
                    <button
                      onClick={() => onOpenGuides(paper.id)}
                      className="bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold text-xs px-3 py-2 rounded-lg transition flex items-center gap-1 cursor-pointer"
                    >
                      <FileText className="w-3.5 h-3.5 text-slate-500" /> Guide
                    </button>
                    {onOpenResources && (
                      <button
                        onClick={() => onOpenResources(paper.id)}
                        className="bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold text-xs px-3 py-2 rounded-lg transition flex items-center gap-1 cursor-pointer"
                      >
                        <Layers className="w-3.5 h-3.5 text-slate-500" /> Flashcards
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {onTryFree && (
                              <button
                                onClick={() => onTryFree(paper.id)}
                                className="w-full bg-[#0E9594] hover:bg-[#0b7f7e] text-white font-bold text-xs py-2 px-3 rounded-lg transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <Play className="w-3.5 h-3.5" /> Try 5 free questions
                              </button>
                            )}
                            <button
                    onClick={openPricingModal}
                    className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs py-2 px-3 rounded-lg transition shadow-xs flex items-center justify-center gap-2"
                  >
                    <Lock className="w-3.5 h-3.5 text-amber-300" /> Unlock with Subscription
                  </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
